type CubicBezier = {
  p1x: number;
  p1y: number;
  p2x: number;
  p2y: number;
};

export type Category = {
  weight: number;
  curve: CubicBezier;
  required: boolean;
  postProcess?: (score: number) => number;
  penalize?: boolean;
  penaltyThreshold?: number; // Below this threshold, penalties apply
  penaltyFactor?: number; // How severely to penalize low scores
};

export type CategoryName =
  | "gameplay"
  | "graphics"
  | "visualDesign"
  | "audio"
  | "music"
  | "narrative"
  | "optimization"
  | "stability"
  | "trueToVision"
  | "innovation"
  | "longevity"
  | "multiplayer"
  | "scale"
  | "value";

export type Categories = {
  [key in CategoryName]: Category;
};

export type CategoryScores = {
  [key in CategoryName]: number;
};

export const RatingCurves = {
  // Low scores don't matter as much.
  VALUE_EXCELLENCE: {
    p1x: 1,
    p1y: 0.9,
    p2x: 0.55,
    p2y: 1
  } as CubicBezier,

  // Low scores and high scores matter more.
  VALUE_EXCELLENCE_PENALIZE_MEDIOCRITY: {
    p1x: 0.5,
    p1y: 0,
    p2x: 0.5,
    p2y: 1
  } as CubicBezier,

  // Low scores are lower
  PENALIZE_MEDIOCRITY: {
    p1x: 0.55,
    p1y: 0,
    p2x: 0,
    p2y: 0.1
  } as CubicBezier,

  // Slight curve. Very low scores are lower and high scores are higher.
  SLIGHT_CURVE: {
    p1x: 0.5,
    p1y: 0.3,
    p2x: 0.5,
    p2y: 0.7
  },

  // Linear scale (no curve)
  LINEAR: {
    p1x: 0.33,
    p1y: 0.33,
    p2x: 0.67,
    p2y: 0.67
  } as CubicBezier
};

export const categories: Categories = {
  gameplay: {
    weight: 0.5,
    curve: RatingCurves.SLIGHT_CURVE,
    required: true,
    penalize: true,
    penaltyThreshold: 4.5,
    penaltyFactor: 1.5
  },
  graphics: {
    weight: 0.1,
    curve: RatingCurves.VALUE_EXCELLENCE,
    required: false
  },
  visualDesign: {
    weight: 0.1,
    curve: RatingCurves.VALUE_EXCELLENCE,
    required: true
  },
  audio: {
    weight: 0.1,
    curve: RatingCurves.VALUE_EXCELLENCE_PENALIZE_MEDIOCRITY,
    required: true,
    penalize: true,
    penaltyThreshold: 4,
    penaltyFactor: 1.5
  },
  music: {
    weight: 0.1,
    curve: RatingCurves.VALUE_EXCELLENCE,
    required: false
  },
  narrative: {
    weight: 0.3,
    curve: RatingCurves.VALUE_EXCELLENCE,
    required: false
  },
  optimization: {
    weight: 0.1,
    curve: RatingCurves.VALUE_EXCELLENCE_PENALIZE_MEDIOCRITY,
    required: false,
    penalize: true,
    penaltyThreshold: 3.5,
    penaltyFactor: 2.0
  },
  stability: {
    weight: 0.1,
    curve: RatingCurves.PENALIZE_MEDIOCRITY,
    required: true,
    penalize: true,
    penaltyThreshold: 4,
    penaltyFactor: 3.0
  },
  trueToVision: {
    weight: 0.1,
    curve: RatingCurves.VALUE_EXCELLENCE,
    required: false
  },
  innovation: {
    weight: 0.1,
    curve: RatingCurves.LINEAR,
    required: false
  },
  longevity: {
    weight: 0.1,
    curve: RatingCurves.LINEAR,
    required: false
  },
  multiplayer: {
    weight: 0.1,
    curve: RatingCurves.LINEAR,
    required: false
  },
  value: {
    weight: 0.2,
    curve: RatingCurves.LINEAR,
    required: false,
    penalize: true,
    penaltyThreshold: 4,
    penaltyFactor: 2.0
  },
  scale: {
    weight: 0.2,
    curve: RatingCurves.LINEAR,
    required: false
  }
};
// Calculates a point on a cubic Bezier curve
function cubicBezierPoint(curve: CubicBezier, t: number): { x: number; y: number } {
  // Ensure t is between 0 and 1
  t = Math.max(0, Math.min(1, t));

  // P0 is (0,0) and P3 is (1,1) for rating curve
  const p0x = 0,
    p0y = 0;
  const p3x = 1,
    p3y = 1;

  // Calculate Bezier point
  const x =
    Math.pow(1 - t, 3) * p0x +
    3 * Math.pow(1 - t, 2) * t * curve.p1x +
    3 * (1 - t) * Math.pow(t, 2) * curve.p2x +
    Math.pow(t, 3) * p3x;

  const y =
    Math.pow(1 - t, 3) * p0y +
    3 * Math.pow(1 - t, 2) * t * curve.p1y +
    3 * (1 - t) * Math.pow(t, 2) * curve.p2y +
    Math.pow(t, 3) * p3y;

  return { x, y };
}

// Finds the parameter t where the Bezier curve's x-coordinate equals the target x
// Uses binary search for numerical approximation
function findParameterForX(curve: CubicBezier, targetX: number, precision: number = 0.0001): number {
  // Handle edge cases
  if (targetX <= 0) return 0;
  if (targetX >= 1) return 1;

  let tMin = 0;
  let tMax = 1;
  let t = 0.5;
  let iterations = 0;
  const MAX_ITERATIONS = 100; // Prevent infinite loops

  while (iterations < MAX_ITERATIONS) {
    const point = cubicBezierPoint(curve, t);
    const error = point.x - targetX;

    // If we're close enough, return this parameter
    if (Math.abs(error) < precision) {
      return t;
    }

    // Adjust search interval
    if (error > 0) {
      tMax = t;
    } else {
      tMin = t;
    }

    // Next midpoint
    t = (tMin + tMax) / 2;
    iterations++;
  }

  // Return best approximation if we reach max iterations
  return t;
}

// Calculates the adjusted weight of a score based on a Bezier curve
export function getWeightedScore(curve: CubicBezier, score: number, maxScore: number = 10): number {
  // Normalize score to 0-1 range
  const normalizedScore = score / maxScore;

  // Find the parameter t where x = normalizedScore
  const t = findParameterForX(curve, normalizedScore);

  // Calculate the corresponding y value
  const result = cubicBezierPoint(curve, t);

  // Scale back to the original range
  return result.y * maxScore;
}

// Applies a penalty to a score if it falls below a certain threshold
function applyPenalty(score: number, config: Category, maxScore: number = 10): number {
  if (!config.penalize) return score;

  const threshold = config.penaltyThreshold || maxScore * 0.4; // Default threshold at 40%
  const factor = config.penaltyFactor || 2.5; // Default penalty multiplier

  if (score < threshold) {
    // Calculate penalty - more severe as score approaches zero
    const normalizedBelowThreshold = (threshold - score) / threshold;
    const penalty = normalizedBelowThreshold * factor * config.weight * maxScore;
    return score - penalty;
  }

  return score;
}

// Give a game's scores in each category and return a final score
export function calculateGameRating(gameScores: { [category: string]: number }): number {
  // Calculate adjusted scores for each category
  type CategoryResult = {
    category: string;
    rawScore: number;
    adjustedScore: number;
    weight: number;
    required: boolean;
    penaltyApplied?: number;
  };

  const categoryResults: CategoryResult[] = [];
  let criticalPenaltyFactor = 1.0; // Multiplier for critical issues

  // Calculate weighted score for each category
  for (const [category, config] of Object.entries(categories)) {
    if (gameScores[category] !== undefined) {
      // Get the base weighted score using the curve
      let rawScore = gameScores[category] - 0.5; // Adjust to 0-9 range
      let adjustedScore = getWeightedScore(config.curve, rawScore);
      let penaltyApplied = 0;

      // Apply custom post-processing if provided
      if (config.postProcess) {
        adjustedScore = config.postProcess(adjustedScore);
      }

      // Apply penalty for low scores in critical categories
      if (config.penalize) {
        const scoreBefore = adjustedScore;
        adjustedScore = applyPenalty(adjustedScore, config);
        penaltyApplied = scoreBefore - adjustedScore;

        // For stability and other critical categories, apply a multiplicative penalty
        if (category === "stability" && rawScore < 3) {
          // Severe stability issues reduce the entire game rating
          criticalPenaltyFactor *= 0.3 + (rawScore / 10) * 0.7;
        }
      }

      categoryResults.push({
        category,
        rawScore,
        adjustedScore,
        weight: config.weight,
        required: config.required,
        penaltyApplied
      });
    }
  }

  // Separate required and non-required categories
  const requiredCategories = categoryResults.filter((result) => result.required);
  const nonRequiredCategories = categoryResults.filter((result) => !result.required);

  // Sort non-required categories by their adjusted scores (ascending)
  nonRequiredCategories.sort((a, b) => a.adjustedScore - b.adjustedScore);

  // Exclude the 3 lowest non-required categories (or fewer if there aren't 3)
  const excludeCount = Math.min(4, nonRequiredCategories.length);
  const includedNonRequiredCategories = nonRequiredCategories.slice(excludeCount);

  // Combine required categories with the remaining non-required categories
  const includedCategories = [...requiredCategories, ...includedNonRequiredCategories];

  // Calculate the final score
  let totalScore = 0;
  let totalWeight = 0;
  let totalPenalty = 0;

  for (const result of includedCategories) {
    totalScore += result.adjustedScore * result.weight;
    totalWeight += result.weight;

    // console.log(
    //   `Category: ${result.category}, Raw Score: ${result.rawScore}, Adjusted Score: ${result.adjustedScore}, Weight: ${result.weight}, Penalty Applied: ${result.penaltyApplied || 0}`
    // );

    if (result.penaltyApplied) {
      totalPenalty += result.penaltyApplied;
    }
  }

  // Handle case where not all categories were scored
  if (totalWeight === 0) return 0;

  // Normalize based on weights used
  let finalScore = totalScore / totalWeight;

  // Apply the critical penalty factor (for things like stability)
  finalScore *= criticalPenaltyFactor;

  // Ensure score stays within 0-10 range
  // Apply small bonus
  return Math.max(0, Math.min(10, finalScore * 1.02));
}
